import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { startTransition, useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import { useMutation, useReadQuery } from '@apollo/client/react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { BackupMemberRoleEnum } from 'src/graphql/schema';
import { BackupMemberRemoveDocument } from 'src/graphql/actions/backupMembers/backupMemberRemove.mutation';
import { withSuspense } from 'src/hoc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { cn } from 'src/lib/utils';
import { useBackup } from 'src/context/backup';

import { getMemberSortPriority } from '../lib';
import { refetchUsers, refetchBackupMembers } from '../api';
import { useMembersManagement } from '../model/MembersManagementContext';

import RoleCell from './RoleCell';

type IMemberRow = {
  id: string;
  memberId: string;
  member: {
    name: string;
    email: string;
  };
  role: BackupMemberRoleEnum;
  action: {
    canRemove: boolean;
    canUpdate: boolean;
  };
};

const RemoveCell = ({ id, canRemove }: { id: string; canRemove: boolean }) => {
  const onCompleted = () =>
    startTransition(() => {
      refetchUsers();
      refetchBackupMembers();
    });

  const [removeBackupMember, { loading }] = useMutation(BackupMemberRemoveDocument, {
    onCompleted,
  });

  const onRemove = async () => {
    try {
      await removeBackupMember({
        variables: {
          backupMemberId: id,
        },
      });
      toast.success('Member access has been removed');
    } catch (error) {
      toast.error('Failed to remove member access');
    }
  };

  if (canRemove) {
    return (
      <Button variant="ghost" onClick={onRemove} disabled={loading} isLoading={loading} size="sm">
        <Trash2 className="text-destructive" />
      </Button>
    );
  }

  return null;
};

const columnHelper = createColumnHelper<IMemberRow>();
const columns = [
  columnHelper.accessor('member', {
    header: 'Team member',
    cell: (props) => {
      const { name, email } = props.getValue();
      const fallback = name.charAt(0).toUpperCase();

      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-8 md:size-10 rounded-full">
            <AvatarImage src={getGravatarUrl(email)} />
            <AvatarFallback className="rounded-full bg-primary text-white">{fallback}</AvatarFallback>
          </Avatar>
          <span className="text-sm text-foreground font-medium line-clamp-1">{name}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('role', {
    header: 'Permissions',
    cell: (props) => (
      <div className="flex justify-end">
        <RoleCell
          role={props.getValue()}
          id={props.row.original.memberId}
          canUpdate={props.row.original.action.canUpdate}
        />
      </div>
    ),
    meta: {
      headerClassName: 'text-right',
    },
  }),
  columnHelper.accessor('action', {
    header: '',
    cell: (props) => <RemoveCell id={props.row.original.memberId} canRemove={props.getValue().canRemove} />,
    meta: {
      headerClassName: 'w-18',
      cellClassName: 'w-18',
    },
  }),
];

const AddedMembers = () => {
  const {
    me: { id: profileId },
  } = useBackup();
  const { queryBackupMembersRef } = useMembersManagement();
  const {
    data: { backupMembers },
  } = useReadQuery(queryBackupMembersRef);

  const membersMapped = useMemo(() => {
    const sortedNodes = backupMembers.nodes.slice().sort((a, b) => {
      const priorityA = getMemberSortPriority(a.user, profileId);
      const priorityB = getMemberSortPriority(b.user, profileId);

      if (priorityA !== priorityB) return priorityA - priorityB;

      return 0;
    });

    return sortedNodes.map((member) => ({
      id: member.user.id,
      memberId: member.id,
      member: {
        name: member.user.name || '-',
        email: member.user.email || '-',
      },
      role: member.role,
      action: {
        canRemove: member.policy.remove,
        canUpdate: member.policy.update,
      },
    }));
  }, [backupMembers.nodes, profileId]);
  const table = useReactTable({
    data: membersMapped,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4 overflow-hidden max-h-100 md:max-h-none flex flex-col px-6">
      <h2 className="text-base text-foreground">Backup members ({backupMembers.nodesCount})</h2>
      <div className="overflow-hidden rounded-sm border border-border grow">
        <Table maxHeight="h-full">
          <TableHeader className="sticky top-0 bg-white z-10 after:absolute after:left-0 after:bottom-0 after:w-full after:border-b after:border-border">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'whitespace-nowrap first:rounded-tl-md last:rounded-tr-md',
                      header.column.columnDef.meta?.headerClassName,
                    )}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(!row.original.action.canUpdate && 'bg-slate-100 hover:bg-slate-100')}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className={cn('cell.column.columnDef.meta?.cellClassName')}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default withSuspense(AddedMembers);
